/**
 * NotifPopup — toaster global de notificaciones en tiempo real (SSE).
 *
 * Se monta una sola vez en el AppShell. Abre un EventSource a
 * `/api/admin/notifications/stream` y, cada vez que llega un evento, apila
 * un toast en la esquina superior-derecha con:
 *   - icono (ShoppingBag para órdenes, Bell para genéricos)
 *   - titulo + mensaje
 *   - link "Ver" (si la notif trae linkUrl)
 *   - boton "Marcar como vista" (POST /api/admin/notifications/mark-read)
 *   - boton X (cierra solo localmente, no marca como leída)
 *
 * Características:
 *   - Apilable (varios toasts visibles a la vez).
 *   - Auto-dismiss configurable por toast (default 30s).
 *   - Sonido tipo "ping" al recibir (Audio + data URI base64, requiere
 *     interacción previa del usuario por políticas de autoplay).
 *   - Accesible: role="alert" + aria-live="assertive".
 *   - Reconexión automática del EventSource (manejada por el browser).
 *
 * NO duplica la API: usa exactamente los mismos endpoints que ya consume
 * el badge del Topbar. NO toca Topbar.tsx.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Bell, ShoppingBag, Package, AlertCircle, X, Check, ExternalLink } from 'lucide-react'
import { apiPost } from '../lib/api'

interface NotifPayload {
  id: string
  userId: string | null
  audience: 'all' | 'admin' | 'operator' | string
  type: string
  title: string
  message: string | null
  linkUrl: string | null
  icon: string | null
  readAt: string | null
  createdAt: string
}

interface Toast extends NotifPayload {
  // id local (puede ser el mismo que id de notif, pero por si llega duplicado)
  _localKey: string
}

const AUTO_DISMISS_MS = 30_000
const MAX_TOASTS = 5

// Beep corto (WAV, ~120ms, 880Hz). Base64 inline para evitar requests externos.
// Generado con un sintetizador básico — suficiente para un "ping" discreto.
const BEEP_DATA_URI =
  'data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA='

function iconFor(type: string, hint: string | null) {
  const k = (hint || type || '').toLowerCase()
  if (k.includes('order') || k.includes('shopping') || k === 'shopping-bag') {
    return <ShoppingBag className="size-5" />
  }
  if (k.includes('stock') || k === 'package') return <Package className="size-5" />
  if (k.includes('alert') || k.includes('warn')) return <AlertCircle className="size-5" />
  return <Bell className="size-5" />
}

export function NotifPopup() {
  const navigate = useNavigate()
  const [toasts, setToasts] = useState<Toast[]>([])
  const esRef = useRef<EventSource | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const seenRef = useRef<Set<string>>(new Set())

  // --- Audio (lazy init) ---
  useEffect(() => {
    try {
      audioRef.current = new Audio(BEEP_DATA_URI)
      audioRef.current.volume = 0.4
    } catch {
      /* sin audio */
    }
  }, [])

  function ping() {
    const a = audioRef.current
    if (!a) return
    try {
      a.currentTime = 0
      const p = a.play()
      if (p && typeof (p as Promise<void>).catch === 'function') {
        ;(p as Promise<void>).catch(() => {
          /* autoplay bloqueado, ignoramos */
        })
      }
    } catch {
      /* ignoramos */
    }
  }

  // --- SSE ---
  useEffect(() => {
    let cancelled = false

    function open() {
      if (cancelled) return
      try {
        const es = new EventSource('/api/admin/notifications/stream', {
          withCredentials: true,
        })
        esRef.current = es

        es.onmessage = (evt) => {
          try {
            const payload = JSON.parse(evt.data) as NotifPayload
            if (!payload || !payload.id) return
            // Sólo nos importan las del admin/operator (audience !== 'user')
            // y evitamos duplicados por id.
            if (seenRef.current.has(payload.id)) return
            seenRef.current.add(payload.id)

            pushToast(payload)
            ping()
          } catch {
            /* payload no parseable */
          }
        }

        es.onerror = () => {
          // El browser intenta reconectar solo. Si quedó cerrado del todo,
          // reabrimos manualmente tras 5s.
          if (es.readyState === EventSource.CLOSED && !cancelled) {
            setTimeout(open, 5000)
          }
        }
      } catch {
        /* sin SSE — no rompemos */
      }
    }

    open()

    return () => {
      cancelled = true
      if (esRef.current) {
        esRef.current.close()
        esRef.current = null
      }
      // limpiar timers
      for (const t of timersRef.current.values()) clearTimeout(t)
      timersRef.current.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function pushToast(n: NotifPayload) {
    const t: Toast = { ...n, _localKey: n.id }
    setToasts((prev) => {
      const next = [t, ...prev]
      // limitar visibles
      return next.slice(0, MAX_TOASTS)
    })

    // auto-dismiss
    const timer = setTimeout(() => {
      removeToast(t._localKey)
    }, AUTO_DISMISS_MS)
    timersRef.current.set(t._localKey, timer)
  }

  function removeToast(localKey: string) {
    setToasts((prev) => prev.filter((x) => x._localKey !== localKey))
    const timer = timersRef.current.get(localKey)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(localKey)
    }
  }

  async function markRead(id: string, localKey: string) {
    try {
      await apiPost('/api/admin/notifications/mark-read', { ids: [id] })
    } catch {
      /* silencioso — igual quitamos el toast */
    }
    removeToast(localKey)
  }

  function goLink(n: Toast) {
    // Marca como leída al navegar
    void markRead(n.id, n._localKey)
    if (n.linkUrl) {
      try {
        navigate({ to: n.linkUrl as any })
      } catch {
        // fallback a window
        window.location.href = n.linkUrl
      }
    }
  }

  if (toasts.length === 0) return null

  return (
    <>
      <div
        aria-live="assertive"
        aria-atomic="true"
        role="region"
        aria-label="Notificaciones nuevas"
        className="np-stack"
      >
        {toasts.map((t) => (
          <div key={t._localKey} role="alert" className="np-toast">
            <div className="np-toast__icon" aria-hidden="true">
              {iconFor(t.type, t.icon)}
            </div>
            <div className="np-toast__body">
              <div className="np-toast__title">{t.title}</div>
              {t.message && <div className="np-toast__msg">{t.message}</div>}
              <div className="np-toast__actions">
                {t.linkUrl && (
                  <button
                    type="button"
                    className="np-toast__btn np-toast__btn--primary"
                    onClick={() => goLink(t)}
                  >
                    <ExternalLink className="size-3.5" />
                    Ver
                  </button>
                )}
                <button
                  type="button"
                  className="np-toast__btn"
                  onClick={() => markRead(t.id, t._localKey)}
                >
                  <Check className="size-3.5" />
                  Marcar como vista
                </button>
              </div>
            </div>
            <button
              type="button"
              className="np-toast__close"
              aria-label="Cerrar notificación"
              onClick={() => removeToast(t._localKey)}
            >
              <X className="size-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Estilos embebidos (CSS modular por componente, sin libs nuevas) */}
      <style>{`
        .np-stack {
          position: fixed;
          top: 16px;
          right: 16px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 10px;
          pointer-events: none;
          max-width: calc(100vw - 32px);
        }
        .np-toast {
          pointer-events: auto;
          width: 360px;
          max-width: 100%;
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-left: 4px solid var(--bosch-primary, #ed0007);
          box-shadow: 0 10px 30px -10px rgba(15, 23, 42, 0.25), 0 4px 10px -4px rgba(15, 23, 42, 0.1);
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 12px 12px 14px;
          animation: np-slide-in 240ms ease-out;
        }
        @keyframes np-slide-in {
          from { transform: translateX(120%); opacity: 0; }
          to   { transform: translateX(0);     opacity: 1; }
        }
        .np-toast__icon {
          flex: 0 0 auto;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: grid;
          place-items: center;
          background: var(--bosch-primary, #ed0007);
          color: #fff;
        }
        .np-toast__body {
          flex: 1 1 auto;
          min-width: 0;
        }
        .np-toast__title {
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
          line-height: 1.25;
          margin: 0 0 2px;
          word-break: break-word;
        }
        .np-toast__msg {
          font-size: 12.5px;
          color: #475569;
          line-height: 1.35;
          margin: 0 0 8px;
          word-break: break-word;
        }
        .np-toast__actions {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 6px;
        }
        .np-toast__btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 5px 10px;
          font-size: 12px;
          font-weight: 500;
          border-radius: 6px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #f8fafc;
          color: #0f172a;
          cursor: pointer;
          transition: background 120ms ease, border-color 120ms ease;
        }
        .np-toast__btn:hover { background: #f1f5f9; }
        .np-toast__btn--primary {
          background: var(--bosch-primary, #ed0007);
          border-color: var(--bosch-primary, #ed0007);
          color: #fff;
        }
        .np-toast__btn--primary:hover {
          background: var(--bosch-primary-dark, #c40006);
          border-color: var(--bosch-primary-dark, #c40006);
        }
        .np-toast__close {
          flex: 0 0 auto;
          background: transparent;
          border: 0;
          padding: 4px;
          color: #64748b;
          cursor: pointer;
          border-radius: 6px;
          transition: background 120ms ease, color 120ms ease;
        }
        .np-toast__close:hover {
          background: #f1f5f9;
          color: #0f172a;
        }
        @media (max-width: 480px) {
          .np-stack { top: 8px; right: 8px; left: 8px; }
          .np-toast { width: 100%; }
        }
      `}</style>
    </>
  )
}
